import { newDateFromUTC, newDateToUTC } from '@bc-library/common'
import type { IElearningModule } from '@bc-library/interface'

// The due date of a module (or of a learning path, copied to its modules) is a business date: a calendar day
// stored at UTC midnight. The other dates are timestamps of the learner's activity.
type WithDueDate = { dueDate?: Date | string | null }

// DATA RECEIVED FROM API

const dueDateFromUTC = (dueDate: Date | string | null | undefined) => dueDate ? newDateFromUTC(dueDate) : null

export const afterReceivingElearningModule = <T extends IElearningModule>(module: T): T => ({
  ...module,
  assignedAt: module.assignedAt ? new Date(module.assignedAt) : null,
  startedAt: module.startedAt ? new Date(module.startedAt) : null,
  completedAt: module.completedAt ? new Date(module.completedAt) : null,
  dueDate: dueDateFromUTC(module.dueDate),
})

export const afterReceivingElearningModules = <T extends IElearningModule>(modules: T[] | null | undefined): T[] =>
  (modules ?? []).map(afterReceivingElearningModule)

/** An assignment, a participant of a course or an enrollment in a learning path: only its due date is converted. */
export const afterReceivingDueDate = <T extends object>(object: T): T =>
  object && typeof object === 'object' && 'dueDate' in object ? { ...object, dueDate: dueDateFromUTC((object as WithDueDate).dueDate) } : object

/** A page of them (`docs`), the rest of the response as is. */
export const afterReceivingDueDatePage = <T extends { docs?: object[] }>(response: T): T =>
  Array.isArray(response?.docs) ? { ...response, docs: response.docs.map(afterReceivingDueDate) } : response

// DATA TO BE SENT TO API

/** The day picked (local midnight) is sent at UTC midnight; nothing changes when there is no `dueDate` key. */
export const beforeSendingDueDate = <T extends object>(data: T): T => {
  const dueDate = (data as WithDueDate | null)?.dueDate
  return data && typeof data === 'object' && 'dueDate' in data ? { ...data, dueDate: dueDate ? newDateToUTC(dueDate) : null } : data
}
